import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { DrawerQueryParams, DrawerRoutes } from '@uigy/shared/components/ui-drawer/drawer.enums';
import { UrlParameterOptions, UrlParametersService } from './url-parameters.service';
import { Subscription } from 'rxjs';

// Allow passing only the keys being updated while keeping strong typing for known DrawerQueryParams
export type SideDrawerParams = Partial<Record<DrawerQueryParams, string | null>> & {
  [key: string]: string | null | undefined;
};

/**
 * Navigation options specific to side drawer parameter updates.
 * updateRouteSnapshot (default true):
 *  - When true, mutates the active route snapshot's queryParams to keep Angular state aligned
 *    with manual history.pushState / replaceState usage.
 */
export interface SideDrawerNavigationOptions extends UrlParameterOptions {
  updateRouteSnapshot?: boolean;
}

export interface RegisteredDrawerHandle {
  open: (id: string, options?: { historyMode?: 'push' | 'replace' }) => void;
  close: (options?: { historyMode?: 'push' | 'replace' }) => void;
  paramKey: DrawerQueryParams;
}

/**
 * Internal registry entry describing a bound drawer instance.
 * Contains callbacks for state application and policies for history mode.
 */
interface SideDrawerRegistryEntry {
  paramKey: DrawerQueryParams;
  getCurrentId: () => string | undefined;
  isOpen: () => boolean;
  onSelect: (id: string, source: 'url' | 'code') => void;
  onClear: (source: 'url' | 'code') => void;
  openHistoryMode: 'push' | 'replace';
  closeHistoryMode: 'push' | 'replace';
}

@Injectable({
  providedIn: 'root'
})
export class SideDrawerUrlParametersService extends UrlParametersService {
  private location = inject(Location);

  private registry = new Map<string, SideDrawerRegistryEntry>();
  private listenersInitialized = false;
  private lastSyncedIds = new Map<string, string | undefined>();
  private popstateListener: (() => void) | null = null;
  private routerEventsSub: Subscription | null = null;

  /**
   * Update (merge or replace) one or more drawer-related query parameters.
   * Uses History API instead of router.navigate to avoid triggering Angular navigation events.
   *
   * Behavior:
   * - Skips if no effective changes (including duplicate writes).
   * - Pushes history entry unless options.historyMode === 'replace'.
   * - Optionally updates the active Angular route snapshot for downstream navigations.
   *
   * @param params Partial mapping of drawer param keys to values (string or null to clear).
   * @param options queryParamsHandling ('merge' | 'replace'), historyMode, updateRouteSnapshot.
   */
  public updateSideDrawerParams(
    params: SideDrawerParams,
    options: SideDrawerNavigationOptions = {}
  ): void {
    const currentParams = this.getCurrentParams();
    const newParams = options.queryParamsHandling === 'merge' ? this.mergeParams(currentParams, params) : params;

    if (!this.hasParameterChanges(newParams)) {
      return;
    }

    // Always push a new history entry (except for true duplicates) so Back restores previous drawer state
    const isDuplicateCall = Object.keys(params).every(key => currentParams[key] === params[key]);
    if (isDuplicateCall) {
      return;
    }

    const url = this.buildUrlWithParams(newParams);
    if (options.historyMode === 'replace') {
      window.history.replaceState(null, '', url);
    } else {
      window.history.pushState(null, '', url);
    }

    if (options.updateRouteSnapshot !== false) {
      this.updateRouteSnapshot(newParams);
      // Keep live params in sync so downstream listeners don't act on stale params
      this.setCurrentParams(newParams);
    }

    // Update cache for affected drawer keys
    Object.keys(params).forEach(key => {
      const value = (newParams as any)[key];
      this.lastSyncedIds.set(key, value === null || value === undefined ? undefined : String(value));
    });
  }

  /**
   * Remove (unset) a set of drawer query parameters and update URL accordingly.
   *
   * @param keys Array of DrawerQueryParams to remove.
   * @param options historyMode push|replace; updateRouteSnapshot (default true).
   */
  public removeSideDrawerParams(
    keys: DrawerQueryParams[],
    options: { historyMode?: 'push' | 'replace'; updateRouteSnapshot?: boolean } = {}
  ): void {
    const currentParams = this.getCurrentParams();
    const newParams = this.removeParams(currentParams, keys);

    if (!this.hasParameterChanges(newParams)) {
      return;
    }

    const url = this.buildUrlWithParams(newParams);
    if (options.historyMode === 'replace') {
      window.history.replaceState(null, '', url);
    } else {
      window.history.pushState(null, '', url);
    }

    if (options.updateRouteSnapshot !== false) {
      this.updateRouteSnapshot(newParams);
      // Keep live params in sync so downstream listeners don't act on stale params
      this.setCurrentParams(newParams);
    }

    // Note: history.pushState doesn't trigger router events, but the side drawer services
    // should handle URL changes through their existing NavigationEnd listeners when browser
    // back/forward is used, or through direct method calls when selecting items

    // Clear cache for removed keys
    keys.forEach(key => this.lastSyncedIds.set(key, undefined));
  }

  /**
   * Retrieve current values for a subset of drawer parameters.
   * @param keys Drawer param keys to extract.
   * @returns SideDrawerParams containing only requested keys (undefined omitted).
   */
  public getCurrentSideDrawerParams(keys: DrawerQueryParams[]): SideDrawerParams {
    const currentParams = this.getCurrentParams();
    return this.extractParams(currentParams, keys);
  }

  /**
   * Check if a specific drawer parameter is present and not null.
   * @param key Drawer param key.
   */
  public hasSideDrawerParam(key: DrawerQueryParams): boolean {
    const currentParams = this.getCurrentParams();
    return currentParams.hasOwnProperty(key) && currentParams[key] !== null;
  }

  /**
   * Get a drawer param value cast to generic type if present.
   * @param key Drawer param key.
   * @param fallback Returned if the value is absent.
   */
  public getSideDrawerParamValue<T>(key: DrawerQueryParams, fallback?: T): T | undefined {
    const currentParams = this.getCurrentParams();
    return this.getParamValue(currentParams, key, fallback);
  }

  /**
   * Set a single drawer parameter (merging with existing query params).
   * @param key Drawer param key.
   * @param value New value or null to clear.
   */
  public setSideDrawerParam(key: DrawerQueryParams, value: string | null): void {
    this.updateSideDrawerParams({ [key]: value }, { updateRouteSnapshot: true, queryParamsHandling: 'merge' });
  }

  /**
   * Clear (remove) a single drawer parameter.
   * @param key Drawer param key.
   */
  public clearSideDrawerParam(key: DrawerQueryParams): void {
    this.setSideDrawerParam(key, null);
  }

  /**
   * Determine if any of the provided drawer parameters are currently set.
   * @param keys Param keys to inspect.
   */
  public hasAnySideDrawerParams(keys: DrawerQueryParams[]): boolean {
    const currentParams = this.getCurrentParams();
    return this.hasAnyParams(currentParams, keys);
  }

  /**
   * Build a full URL (path + query string) with supplied params applied.
   * @param params Parameter set to encode.
   * @returns URL string safe for history.pushState / replaceState.
   */
  private buildUrlWithParams(params: Params): string {
    // Use window.location.pathname to preserve the actual browser URL path
    // This ensures we only modify query parameters, not the path itself
    const baseUrl = typeof window !== 'undefined' ? window.location.pathname : this.location.path().split('?')[0];

    // Preserve hash fragment if it exists
    const hash = typeof window !== 'undefined' ? window.location.hash : '';

    const queryString = this.buildQueryString(params);
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
    return hash ? `${url}${hash}` : url;
  }

  /**
   * Convert a param object into a query string.
   * Skips null/undefined and empty trimmed strings.
   * Serializes objects via JSON.stringify (reserved for advanced use cases).
   * @param params Param object.
   */
  private buildQueryString(params: Params): string {
    const queryParts: string[] = [];

    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined) {
        if (typeof value === 'string' && value.trim() !== '') {
          queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        } else if (typeof value === 'object') {
          queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`);
        }
      }
    }

    return queryParts.join('&');
  }

  /**
   * Mutate the active route snapshot to reflect new query parameters.
   * Required because we bypass Angular Router navigation.
   * @param newParams Effective new param map.
   */
  private updateRouteSnapshot(newParams: Params): void {
    // This is a workaround to update Angular's in-memory route snapshot
    // so that future router.navigate calls work correctly
    const route = this.getCurrentRoute() as any;
    if (route && route.snapshot) {
      route.snapshot.queryParams = { ...newParams };
    }
  }

  /**
   * Overridden: not used for side drawers.
   * Throws to prevent accidental usage of router-based navigation path.
   */
  protected createNavigationPromise(change: UrlParameterOptions): Promise<boolean> {
    // For side drawers, we use history.pushState instead of router.navigate
    // This method is provided for compatibility but should not be used
    throw new Error('Side drawer service uses history.pushState instead of router.navigate. Use updateSideDrawerParams instead.');
  }

  /**
   * Register a drawer so its open/close state maps to a specific query parameter.
   * Performs an initial sync from current URL (without mutation).
   *
   * @param options.paramKey Query param key representing this drawer.
   * @param options.getCurrentId Returns the currently active entity id (if any).
   * @param options.isOpen Returns true if the drawer UI is open.
   * @param options.onSelect Callback when selecting/opening via URL or code.
   * @param options.onClear Callback when clearing/closing via URL or code.
   * @param options.openHistoryMode push|replace strategy when opening (default push).
   * @param options.closeHistoryMode push|replace when closing (default replace).
   * @returns Handle with open/close helpers.
   */
  public registerDrawer(options: {
    paramKey: DrawerQueryParams;
    getCurrentId: () => string | undefined;
    isOpen: () => boolean;
    onSelect: (id: string, source: 'url' | 'code') => void;
    onClear: (source: 'url' | 'code') => void;
    openHistoryMode?: 'push' | 'replace';
    closeHistoryMode?: 'push' | 'replace';
  }): RegisteredDrawerHandle {
    const entry: SideDrawerRegistryEntry = {
      paramKey: options.paramKey,
      getCurrentId: options.getCurrentId,
      isOpen: options.isOpen,
      onSelect: options.onSelect,
      onClear: options.onClear,
      openHistoryMode: options.openHistoryMode ?? 'push',
      closeHistoryMode: options.closeHistoryMode ?? 'replace',
    };

    this.registry.set(entry.paramKey, entry);
    this.initializeGlobalListenersOnce();

    // Initial sync (no URL mutation)
    const params = this.getCurrentParams();
    const initialId = params[entry.paramKey] || undefined;
    if (initialId) {
      entry.onSelect(initialId, 'url');
    }
    this.lastSyncedIds.set(entry.paramKey, initialId);

    return {
      paramKey: entry.paramKey,
      open: (id: string, opts): void => {
        this.updateSideDrawerParams(
          { [entry.paramKey]: id },
          { historyMode: opts?.historyMode ?? entry.openHistoryMode, queryParamsHandling: 'merge' }
        );
        entry.onSelect(id, 'code');
      },
      close: (opts): void => {
        this.removeSideDrawerParams(
          [entry.paramKey],
          { historyMode: opts?.historyMode ?? entry.closeHistoryMode }
        );
        entry.onClear('code');
      }
    };
  }

  /**
   * Attach global listeners (currently only popstate) once.
   * On popstate:
   *  - Re-parse URL
   *  - Sync internal param cache
   *  - Reconcile drawer open/close state
   */
  private initializeGlobalListenersOnce(): void {
    if (this.listenersInitialized) return;
    this.listenersInitialized = true;

    // Handle browser back/forward navigation
    this.popstateListener = () => this.syncAllFromUrl('url');
    window.addEventListener('popstate', this.popstateListener);

    // Handle router navigations completing (e.g., grid navigations)
    // Use 'code' source to indicate this is from Angular router, not browser navigation
    this.routerEventsSub = this.getRouter().events.subscribe((e: any) => {
      if (e && e.url) {

        this.syncAllFromUrl('code');

        // Remove listeners not on a drawer route (navigating out of page)
        const isDrawerRoute = DrawerRoutes.some(route => this.router.url.includes(route as string));
        if (!isDrawerRoute) {
          this.removeGlobalListeners();
        }
      }
    });
  }

  /**
   * Detach all global listeners related to side drawer URL synchronization.
   * Removes the browser popstate event handler and unsubscribes from Angular router events.
   * Resets internal flags to allow re-initialization when needed.
   */
  private removeGlobalListeners(): void {
    if (this.popstateListener) {
      window.removeEventListener('popstate', this.popstateListener);
      this.popstateListener = null;
      this.listenersInitialized = false;
    }

    if (this.routerEventsSub) {
      this.routerEventsSub.unsubscribe();
      this.routerEventsSub = null;
    }
  }

  /**
   * Synchronize every registered drawer against current query params.
   * Invoked on popstate or router navigation. Only triggers callbacks when an id actually changed
   * compared to lastSyncedIds to avoid redundant UI work.
   * @param source 'url' for browser navigation, 'code' for Angular router navigation.
   */
  private syncAllFromUrl(source: 'url' | 'code'): void {
    // For browser back/forward navigation, read params directly from URL
    // instead of relying on Angular route cache which may be stale
    const params = source === 'url' ? this.getParamsFromLocation() : this.getCurrentParams();

    this.registry.forEach(entry => {
      const id = params[entry.paramKey] || undefined;
      const cached = this.lastSyncedIds.get(entry.paramKey);
      if (id === cached) {
        return; // No change for this drawer param
      }

      if (id) {
        if (!entry.isOpen() || entry.getCurrentId() !== id) {
          entry.onSelect(id, source);
        }
      } else {
        if (entry.isOpen()) {
          entry.onClear(source);
        }
      }

      // Update cache after applying
      this.lastSyncedIds.set(entry.paramKey, id);
    });

    // Update the Angular route cache to keep it in sync with actual URL
    if (source === 'url') {
      this.setCurrentParams(params);
      this.updateRouteSnapshot(params);
    }
  }

  /**
   * Parse window.location.search into a Params object (no type coercion).
   * @returns Parsed Params map.
   */
  private getParamsFromLocation(): Params {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const urlParams = new URLSearchParams(search);
    const params: Params = {};
    urlParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }
}
